<script setup lang="ts">
import { onBeforeUnmount, ref, watch } from 'vue';
import type { InputOtpChangeEvent } from 'primevue';

import UiResendButton from './UiResendButton.vue';

defineOptions({ name: 'UiOtpVerification' });

interface Props {
  appearance?: 'default' | 'dialog';
  cancelLabel?: string;
  inputLabel?: string;
  otpLength?: number;
  resetKey?: number | string;
  resendPrompt?: string;
  resendCooldownSeconds?: number;
  subtitle?: string;
  supportLabel?: string;
  title?: string;
}

const props = withDefaults(defineProps<Props>(), {
  appearance: 'default',
  cancelLabel: 'Cancel',
  inputLabel: 'Enter Code Below',
  otpLength: 6,
  resetKey: 0,
  resendPrompt: "Didn't receive a code?",
  resendCooldownSeconds: 20,
  subtitle:
    'We sent a verification code to your phone number, please enter it below to verify that it is you.',
  supportLabel: 'Contact our support team',
  title: 'SMS Authentication',
});

const emit = defineEmits<{
  confirm: [otp: string];
  cancel: [];
  resend: [];
  support: [];
}>();

const otpValue = ref<string>('');
let confirmTimeout: ReturnType<typeof setTimeout> | undefined;

const clearConfirmTimeout = () => {
  if (confirmTimeout === undefined) {
    return;
  }

  clearTimeout(confirmTimeout);
  confirmTimeout = undefined;
};

watch(
  () => props.resetKey,
  () => {
    clearConfirmTimeout();
    otpValue.value = '';
  },
);

function handleOtpChange(event: InputOtpChangeEvent) {
  otpValue.value = event.value as string;

  clearConfirmTimeout();

  if (otpValue.value.length !== props.otpLength) {
    return;
  }

  confirmTimeout = setTimeout(() => {
    emit('confirm', otpValue.value);
    confirmTimeout = undefined;
  }, 500);
}

onBeforeUnmount(clearConfirmTimeout);

function handleCancel() {
  emit('cancel');
}

function handleResend() {
  emit('resend');
}

function handleSupport() {
  emit('support');
}
</script>

<template>
  <div
    class="verify-otp"
    :class="{
      'dots-pattern': appearance === 'default',
      'verify-otp--dialog': appearance === 'dialog',
    }"
  >
    <div class="verify-otp__cancel">
      <Button
        size="small"
        severity="secondary"
        text
        class="verify-otp__cancel-btn"
        @click="handleCancel"
      >
        <i class="pi pi-times verify-otp__cancel-icon" />
        <span class="verify-otp__cancel-text">{{ cancelLabel }}</span>
      </Button>
    </div>

    <!-- Main Content -->
    <div class="verify-otp__content">
      <!-- Header Section -->
      <div class="verify-otp__header">
        <h3 class="verify-otp__title">{{ title }}</h3>
        <p class="verify-otp__subtitle">{{ subtitle }}</p>
      </div>

      <!-- OTP Input Section -->
      <div class="verify-otp__input-section">
        <p class="verify-otp__input-label">{{ inputLabel }}</p>
        <InputOtp
          v-model="otpValue"
          :length="otpLength"
          class="verify-otp__input"
          integerOnly
          @change="handleOtpChange"
        />
      </div>

      <!-- Resend Section -->
      <div class="verify-otp__resend-section">
        <p class="verify-otp__resend-label">{{ resendPrompt }}</p>
        <div class="verify-otp__resend-actions">
          <UiResendButton
            outlined
            severity="secondary"
            :cooldown-seconds="resendCooldownSeconds"
            :reset-key="resetKey"
            restart-on-click
            class="verify-otp__resend-btn"
            @resend="handleResend"
          />

          <Button
            :label="supportLabel"
            text
            class="verify-otp__support-btn"
            @click="handleSupport"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.verify-otp {
  display: flex;
  flex-direction: column;
  border-radius: $radius-3xl;

  &__cancel-btn {
    margin: $static-space-4 $static-space-2;
    font-weight: $font-medium;
    letter-spacing: var(--p-letter-spacing-tight-1);
    width: fit-content;
    align-self: flex-end;
  }

  &__content {
    display: flex;
    flex-direction: column;
    gap: $static-space-8;
    padding: 0 $static-space-10;
  }

  &__header {
    display: flex;
    flex-direction: column;
    gap: $static-space-4;
  }

  &__title {
    text-align: center;
    color: #334155;
    font-family: var(--font-family-base);
    font-size: $text-4xl;
    font-style: normal;
    font-weight: $font-semibold;
    line-height: $leading-none;
    letter-spacing: $tracking-body;
  }

  &__subtitle {
    line-height: 1.4;
    color: #64748b;
    text-align: center;
    margin: 0;
    max-width: 100%;
  }

  &__input-section {
    display: flex;
    flex-direction: column;
    gap: $static-space-4;
    align-items: center;
  }

  &__input-label {
    font-weight: $font-semibold;
    font-size: $static-text-lg;
    line-height: $leading-none;
    color: #334155;
    text-align: center;
    margin: 0;
  }

  &__input {
    :deep(.p-inputtext) {
      width: 2.625rem;
      height: 2.5rem;
      border-radius: $radius-xl;
      font-weight: $font-semibold;
      font-size: $static-text-lg;
      color: #334155;
    }
  }

  &__resend-section {
    display: flex;
    flex-direction: column;
    gap: $static-space-3;
    align-items: center;
  }

  &__resend-label {
    line-height: $leading-none;
    color: #334155;
    margin: 0;
  }

  &__resend-actions {
    display: flex;
    gap: 0;
    align-items: center;
  }

  &__resend-btn {
    border-radius: var(--p-border-radius-absolut);
    font-size: $static-text-sm;
    opacity: 1;
    min-width: 6.5rem;
    white-space: nowrap;

    :deep(.p-button-label) {
      white-space: nowrap;
    }

    &:disabled {
      opacity: 0.7;
      background-color: #f1f5f9;
      border-color: #f1f5f9;
      color: #475569;
    }
  }

  &__support-btn {
    border-radius: var(--p-border-radius-absolut);
    font-weight: $font-medium;
    font-size: $static-text-sm;
    color: var(--p-primary-color);
    letter-spacing: var(--p-letter-spacing-tight-1);
  }

  &--dialog {
    align-items: center;
    gap: $static-space-8;
    border-radius: $radius-3xl;
  }

  &--dialog &__cancel {
    display: flex;
    align-self: stretch;
    justify-content: flex-end;
    height: $static-space-12;
    padding: $static-space-3 $static-space-3 0;
  }

  &--dialog &__cancel-btn {
    align-self: auto;
    justify-content: center;
    width: 2.1875rem;
    height: $static-space-8;
    margin: 0;
    padding: 0;
    border-radius: $radius-full;
    color: var(--p-surface-500);

    :deep(.p-button-label) {
      display: none;
    }
  }

  &--dialog &__cancel-text:empty {
    display: none;
  }

  &--dialog &__cancel-icon {
    font-size: $static-text-sm;
  }

  &--dialog &__content {
    align-items: center;
    gap: $static-space-8;
    width: 100%;
    padding: 0 $static-space-10;
  }

  &--dialog &__header {
    align-items: center;
    gap: $static-space-4;
    width: 100%;
  }

  &--dialog &__title {
    margin: 0;
    color: var(--p-surface-900);
    font-size: $static-text-xl;
    line-height: $leading-none;
    letter-spacing: $tracking-tight;
  }

  &--dialog &__subtitle {
    color: var(--p-surface-600);
    font-size: $static-text-sm;
    line-height: $leading-none;
    letter-spacing: $tracking-tight;
  }

  &--dialog &__input-section {
    gap: $static-space-4;
  }

  &--dialog &__input-label {
    color: var(--p-surface-900);
    font-size: $static-text-lg;
    font-weight: $font-regular;
    line-height: $leading-none;
    letter-spacing: $tracking-tight;
  }

  &--dialog &__input {
    display: flex;
    gap: 0.4375rem;

    :deep(.p-inputtext) {
      width: 2.625rem;
      height: $static-space-10;
      border-color: var(--p-surface-300);
      border-radius: $radius-xl;
      background: var(--p-surface-0);
      box-shadow: 0 1px 2px 0 rgba(18, 18, 23, 0.05);
      color: var(--p-surface-900);
      font-size: $static-text-lg;
      font-weight: $font-medium;
    }
  }

  &--dialog &__resend-section {
    gap: $static-space-3;
  }

  &--dialog &__resend-label {
    width: 21.1875rem;
    color: var(--p-surface-900);
    font-size: $static-text-sm;
    line-height: $leading-none;
    letter-spacing: $tracking-tight;
    text-align: center;
  }

  &--dialog &__resend-actions {
    justify-content: center;
  }

  &--dialog &__resend-btn,
  &--dialog &__support-btn {
    height: $static-space-8;
    border-radius: $radius-full;
    font-size: $static-text-sm;
    font-weight: $font-medium;
    letter-spacing: $tracking-tight;
  }

  &--dialog &__resend-btn:disabled {
    opacity: 0.7;
    background-color: var(--p-surface-100);
    border-color: var(--p-surface-100);
    color: var(--p-surface-700);
  }

  &--dialog &__support-btn {
    color: var(--p-purple-500);
  }
}
</style>
